import { createContext, FC, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

/** Spotlight data used for analytics base properties */
export interface SpotlightAnalyticsData {
	id: string;
	userId: string;
	startTime?: string;
	endTime?: string;
}

/** Base properties automatically included on all events */
interface SpotlightBaseProperties {
	spotlight_id: string;
	user_id: string;
	spotlight_start_date?: string;
	spotlight_end_date?: string;
	spotlight_month_span?: number;
}

/** Calculate months between two dates (inclusive) */
const calculateMonthSpan = (startTime?: string, endTime?: string): number | undefined => {
	if (!startTime || !endTime) return undefined;
	const start = new Date(startTime);
	const end = new Date(endTime);
	return (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
};

/** Format date as "Month Year" */
const formatMonthYear = (dateString?: string): string | undefined => {
	if (!dateString) return undefined;
	const date = new Date(dateString);
	return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

/** Compute base properties from spotlight data */
const computeBaseProperties = (spotlight?: SpotlightAnalyticsData): SpotlightBaseProperties | undefined => {
	if (!spotlight) return undefined;
	return {
		spotlight_id: spotlight.id,
		user_id: spotlight.userId,
		spotlight_start_date: formatMonthYear(spotlight.startTime),
		spotlight_end_date: formatMonthYear(spotlight.endTime),
		spotlight_month_span: calculateMonthSpan(spotlight.startTime, spotlight.endTime),
	};
};

// --- Context ---

interface SpotlightAnalyticsContextValue {
	spotlightData: SpotlightAnalyticsData | undefined;
	setSpotlightData: (data: SpotlightAnalyticsData) => void;
}

const SpotlightAnalyticsContext = createContext<SpotlightAnalyticsContextValue | undefined>(undefined);

/**
 * Provider that enables spotlight analytics context for child components.
 * Wrap your spotlight viewer with this provider to enable context-based analytics.
 *
 * IMPORTANT: Use `key={spotlightId}` to ensure the provider remounts with fresh
 * state when navigating between spotlights. Without this, stale analytics data
 * from the previous spotlight may be used.
 *
 * Usage:
 * ```tsx
 * <SpotlightAnalyticsProvider key={spotlightId}>
 *   <UserSpotlightViewer spotlightId={spotlightId} />
 * </SpotlightAnalyticsProvider>
 * ```
 */
export const SpotlightAnalyticsProvider: FC<PropsWithChildren> = ({ children }) => {
	const [spotlightData, setSpotlightData] = useState<SpotlightAnalyticsData | undefined>(undefined);

	const value = useMemo(() => ({ spotlightData, setSpotlightData }), [spotlightData]);

	return <SpotlightAnalyticsContext.Provider value={value}>{children}</SpotlightAnalyticsContext.Provider>;
};

/**
 * Hook to access and set the spotlight analytics context.
 * Use this to set the spotlight data when it becomes available.
 *
 * Usage:
 * ```tsx
 * const { setSpotlightData } = useSpotlightAnalyticsContext();
 *
 * useEffect(() => {
 *   if (spotlight) {
 *     setSpotlightData({
 *       id: spotlight.id,
 *       userId: spotlight.userId,
 *       startTime: spotlight.startTime?.toISOString(),
 *       endTime: spotlight.endTime?.toISOString(),
 *     });
 *   }
 * }, [spotlight, setSpotlightData]);
 * ```
 */
export const useSpotlightAnalyticsContext = () => {
	const context = useContext(SpotlightAnalyticsContext);
	if (!context) {
		throw new Error('useSpotlightAnalyticsContext must be used within a SpotlightAnalyticsProvider');
	}
	return context;
};

// --- Analytics Hook ---

/**
 * Analytics hook for Spotlight components that automatically includes
 * spotlight context (id, user, date range) on all tracked events.
 *
 * When used within a SpotlightAnalyticsProvider, it automatically reads
 * the spotlight data from context. No parameters needed.
 *
 * Usage:
 * ```tsx
 * // Inside SpotlightAnalyticsProvider - no params needed
 * const { trackSpotlightEntryViewed, trackSpotlightScrollDepth, trackSpotlightRecognitionCtaClicked } = useSpotlightAnalytics();
 *
 * // All events automatically include spotlight_id, user_id, dates, and month_span
 * trackSpotlightEntryViewed({ entry_point: 'email' });
 * trackSpotlightScrollDepth(50); // Track 50% scroll depth milestone
 * trackSpotlightRecognitionCtaClicked({ cta_type: 'create_recognition', viewer_type: 'manager' });
 * ```
 */
export const useSpotlightAnalytics = () => {
	const { track } = useAnalytics();
	const context = useContext(SpotlightAnalyticsContext);
	const spotlightData = context?.spotlightData;

	// Compute base properties once when spotlight data changes
	const baseProperties = useMemo(() => computeBaseProperties(spotlightData), [spotlightData]);

	const trackSpotlightEntryViewed = useCallback(
		(eventProps?: { entry_point?: string }) => {
			track(AnalyticsEvent.Spotlight.EntryViewed, {
				...baseProperties,
				...eventProps,
			});
		},
		[track, baseProperties]
	);

	const trackSpotlightScrollDepth = useCallback(
		(scrollPercent: number) => {
			track(AnalyticsEvent.Spotlight.ScrollDepth, {
				...baseProperties,
				scroll_percent: scrollPercent,
			});
		},
		[track, baseProperties]
	);

	const trackSpotlightRecognitionCtaClicked = useCallback(
		(props: { cta_type: 'create_recognition' | 'send_spotlight' | 'thank_team'; viewer_type: 'viewer' | 'recipient' | 'manager' }) => {
			track(AnalyticsEvent.Spotlight.RecognitionCtaClicked, {
				...baseProperties,
				...props,
			});
		},
		[track, baseProperties]
	);

	const trackSpotlightSectionEditClicked = useCallback(
		(props: { spotlight_section_id: string; section_category: string }) => {
			track(AnalyticsEvent.Spotlight.SectionEditClicked, {
				...baseProperties,
				...props,
			});
		},
		[track, baseProperties]
	);

	const trackSpotlightSectionEdited = useCallback(
		(props: { spotlight_section_id: string; section_category: string; previous_text: string; new_text: string }) => {
			track(AnalyticsEvent.Spotlight.SectionEdited, {
				...baseProperties,
				...props,
			});
		},
		[track, baseProperties]
	);

	const trackSpotlightSectionDeleted = useCallback(
		(props: { spotlight_section_id: string }) => {
			track(AnalyticsEvent.Spotlight.SectionDeleted, {
				...baseProperties,
				...props,
			});
		},
		[track, baseProperties]
	);

	return {
		trackSpotlightEntryViewed,
		trackSpotlightScrollDepth,
		trackSpotlightRecognitionCtaClicked,
		trackSpotlightSectionEditClicked,
		trackSpotlightSectionEdited,
		trackSpotlightSectionDeleted,
	};
};
